"use client";

import { useState, useEffect, useMemo } from "react";
import styles from "../../../../styles/case-manager-modules/caseManagerDashboard.module.css";
import Table from "../../../../components/table";
import { convertUTCToLocal, formatDate } from "../../../../utils/helper";
import { formatReadingData, filterRecords, groupAndSortRecords } from "../../../../utils/outOfRangeHelpers";
import SendToProviderModal from "../../../../components/case-manager/PatientsModal";
import Modal from "../../../../components/shared-modules/Modal";
import { Trash2, Send, Search, FileDown, CloudCog, Eye, Filter, ChevronDown, ChevronUp, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import {
  getAllPatientReadings,
  getPatientOutOfRangeStats,
} from "../../../../services/admin-case-manager/readings";
import Spinner from "../../../../components/spinner";
import Button from "../../../../components/button-v1";
import toast from "react-hot-toast";
import axios from "axios";
import { BASE_URL } from "../../../../utils/constants";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../../../components/ui/select";

const OutOfRange = () => {
  const [filter, setFilter] = useState("escalated");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedPatientsToSend, setSelectedPatientsToSend] = useState([]);
  const [nameSearch, setNameSearch] = useState("");
  const [providerSearch, setProviderSearch] = useState("");
  const [locationSearch, setLocationSearch] = useState("");
  const [categorySearch, setCategorySearch] = useState("");
  const [tableData, setTableData] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [isLoadingProgress, setIsLoadingProgress] = useState(true);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState("");
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [dateRange, setDateRange] = useState("1week");
  const getToken = () => localStorage.getItem("authToken");
  const getTenant = () => sessionStorage.getItem("acc_id");

  const {
    data: readingsData,
    isLoading: isReadingsLoading,
    refetch: refetchReadings,
  } = useQuery({
    queryKey: ["patientReadings", dateRange],
    queryFn: () => getAllPatientReadings(dateRange),
    staleTime: 0,
    cacheTime: 0,
  });
  const fetchPatients = async () => {
    const token = getToken();
    const tenant = getTenant();

    const url = `${BASE_URL}/patients/${tenant}/patients/?type=all`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  };


  useEffect(() => {
    const getPatients = async () => {
      setIsLoadingProgress(true);
      try {
        const patientData = await fetchPatients();
        const allPatients = patientData?.patients || [];
        const totalPatients = allPatients?.length;

        const outOfRangeCount = allPatients?.filter(
          (p) => p.out_of_range
        ).length;
        const outOfRangePercent =
          totalPatients > 0
            ? Math.round((outOfRangeCount / totalPatients) * 100)
            : 0;
        const completionPercent =
          totalPatients > 0 ? 100 - outOfRangePercent : 0;
        setCompletionPercentage(completionPercent);
      } catch (error) {
        console.error("Error fetching patients:", error);
      } finally {
        setIsLoadingProgress(false);
      }
    };
    getPatients();
  }, []);

  useEffect(() => {
    setTableData(formatReadingData(readingsData ?? []) || []);
  }, [readingsData]);

  // Extract unique options from API data
  const uniqueNames = useMemo(() => {
    if (!readingsData || readingsData.length === 0) return [];
    const names = readingsData
      .map((reading) => reading.patient_full_name)
      .filter((name) => name && name.trim() !== "");
    return [...new Set(names)].sort();
  }, [readingsData]);

  const uniqueProviders = useMemo(() => {
    if (!readingsData || readingsData.length === 0) return [];
    const providers = readingsData
      .map((reading) => reading.provider_full_name)
      .filter((provider) => provider && provider.trim() !== "");
    return [...new Set(providers)].sort();
  }, [readingsData]);

  const uniqueLocations = useMemo(() => {
    if (!readingsData || readingsData.length === 0) return [];
    const locations = readingsData
      .map((reading) => reading.patient_location)
      .filter((location) => location && location.trim() !== "");
    return [...new Set(locations)].sort();
  }, [readingsData]);

  useEffect(() => {
    setNameSearch("");
    setProviderSearch("");
    setLocationSearch("");
    setCategorySearch("");
  }, [filter]);

  const filteredData = useMemo(() => {
    const baseFiltered = filterRecords(tableData, {
      nameSearch,
      providerSearch,
      locationSearch,
      categorySearch,
    });

    // Apply filter (escalated/reviewed)
    const filtered = baseFiltered?.filter((row) => {
      if (filter === "escalated" && row.esclated !== true) return false;
      if (filter === "reviewed" && row.reviewed !== true) return false;
      return true;
    });

    // Group and sort records by patient name
    return groupAndSortRecords(filtered);
  }, [tableData, nameSearch, providerSearch, locationSearch, categorySearch, filter]);

  const handleSendPatients = (patients) => {
    if (patients.length > 0) {
      setIsModalOpen(true);

      setSelectedPatientsToSend(patients);
    } else {
      alert("No patients selected");
    }
  };

  const handleSendSelected = () => {
    const selectedPatients = tableData.filter((row) =>
      selectedIds.includes(row.id)
    );
    handleSendPatients(selectedPatients);
  };

  const handleSendAll = () => {
    handleSendPatients(filteredData);
  };

  const handleSendSingle = (patient) => {
    handleSendPatients([patient]);
  };

  const handleSelectionChange = (ids) => {
    setSelectedIds(ids);

    if (ids.length > 0) {
      const firstSelectedItem = tableData.find((row) => row.id === ids[0]);
      if (firstSelectedItem) {
        setSelectedProvider(firstSelectedItem.provider);
      }
    } else {
      setSelectedProvider(null);
    }
  };

  const handleNameSearchChange = (value) => {
    setNameSearch(value || "");
  };

  const handleProviderSearchChange = (value) => {
    setProviderSearch(value || "");
  };

  const handleLocationSearchChange = (value) => {
    setLocationSearch(value || "");
  };

  const handleCategorySearchChange = (e) => {
    setCategorySearch(e.target.value);
  };
   const generatePDF = () => {
    // Build URL with query params
    const params = new URLSearchParams({
      ...(nameSearch && { nameSearch }),
      ...(providerSearch && { providerSearch }),
      ...(locationSearch && { locationSearch }),
      ...(categorySearch && { categorySearch }),
      filter: filter, // Pass the current filter (escalated/reviewed)
      timeRange: dateRange, // Pass the current date range
    });

    const url = `/patient/out-of-range-pdf?${params.toString()}`;
    window.open(url, "_blank");
  };

  const columns = useMemo(() => [
    { key: "name", label: "Name", alignLeft: true },
    { key: "location", label: "Location", sorting: false },
    { key: "provider", label: "Provider", alignLeft: true },
    { key: "date", label: "Reading Date", alignLeft: true },
    {
      key: "reading_value",
      label: "Reading",
      alignLeft: true,
    },
    { key: "type", label: "Type", alignLeft: true },
    {
      key: "category",
      label: "Category",
      alignLeft: true,
      sorting: false,
      render: (row) => {
        if (Array.isArray(row.category)) {
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              {row.category.map((cat, index) => (
                <div key={index}>{cat}</div>
              ))}
            </div>
          );
        }
        return <div>{row.category || "_ _"}</div>;
      },
    },
    {
      key: "escalated_reviewed_at",
      label: filter === "escalated" ? "Escalated At" : "Reviewed At",
      alignLeft: true,
      sorting: false,
      render: (row) => {
        const date = row.escalated_at || row.reviewed_at;
        return <div>{date ? convertUTCToLocal(date) : "_ _"}</div>;
      },
    },
    {
      key: "interaction",
      label: "Interaction Notes",
      sorting: false,
      render: (row) => {
        let noteText = "";
        if (filter === "escalated") {
          noteText = row.escalation_note || "";
        } else if (filter === "reviewed") {
          noteText = row.review_note || "";
        } else {
          noteText = row.interaction || "";
        }

        if (!noteText || noteText === "_ _") {
          return <div>_ _</div>;
        }

        return (
          <button
            className={styles.icon_button}
            onClick={() => {
              setSelectedNote(noteText);
              setIsNoteModalOpen(true);
            }}
            style={{ background: "none", border: "none", cursor: "pointer", padding: "4px" }}
          >
            <Eye size={18} />
          </button>
        );
      },
    },
    {
      key: "send",
      label: "Send",
      sorting: false,
      alignLeft: false,
      render: (row) => (
        <div className={styles.action_buttons}>
          {/* <button className={styles.icon_button} onClick={() => handleDelete(row.id)}>
            <Trash2 size={15} />
          </button> */}
          <button
            className={styles.icon_button}
            onClick={() => handleSendSingle(row)}
          >
            <Send size={15} />
          </button>
        </div>
      ),
    },
  ], [filter]);

  return (
    <div className={styles.patient_page}>
     
      <div className={styles.bp_header}>
        <div className={styles.progressSection}>
        {!(isLoadingProgress || isReadingsLoading) && (
         <p className={styles.progressText}>
         <strong>{completionPercentage}% </strong> Out of Range Completed
         </p>
           )}

           {isLoadingProgress || isReadingsLoading ? (
         <div className={styles.progressBar}>
    <div className={styles.progressFillLoading}>
      <span className={styles.loadingText}>Calculating progress...</span>
    </div>
         </div>
          ) : (
          <div className={styles.progressBar}>
         <div
          className={styles.progressFill}
          style={{ width: `${completionPercentage}%` }}
         />
       </div>
         )}

          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
           
            <button
              onClick={() => setIsSearchExpanded(!isSearchExpanded)}
              className={`${styles.search_input} ${styles.filter_button}`}
            >
              <Search size={16} className={styles.filter_button_icon} />
              <span className={styles.filter_button_text}>
                {isSearchExpanded ? "Hide Filters" : "Show Filters"}
              </span>
              {isSearchExpanded ? (
                <ChevronUp size={16} className={styles.filter_button_icon} />
              ) : (
                <ChevronDown size={16} className={styles.filter_button_icon} />
              )}
            </button>
            <Button
              variant="ghost"
              onClick={generatePDF}
              disabled={tableData?.filter((row) => row.esclated === true || row.reviewed === true).length === 0}
            >
              <FileDown size={16} style={{ marginRight: "4px" }} />
              Report Download
            </Button>
          </div>

          <div className={`${styles.search_container_expanded} ${isSearchExpanded ? styles.search_container_expanded_visible : ''}`}>
          <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
              <Select
                value={locationSearch}
                onChange={handleLocationSearchChange}
                placeholder="Select Location Name"
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Location Name">
                    {locationSearch ? locationSearch : undefined}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent searchField={true}>
                  <SelectItem value="">All Locations</SelectItem>
                  {uniqueLocations.map((location) => (
                    <SelectItem key={location} value={location}>
                      {location}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {locationSearch && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLocationSearchChange("");
                  }}
                  className={styles.clear_select_button}
                >
                  <X size={14} color="#666" />
                </button>
              )}
            </div>
           
            <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
              <Select
                value={providerSearch}
                onChange={handleProviderSearchChange}
                placeholder="Select Provider Name"
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Provider Name">
                    {providerSearch ? providerSearch : undefined}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent searchField={true}>
                  <SelectItem value="">All Providers</SelectItem>
                  {uniqueProviders.map((provider) => (
                    <SelectItem key={provider} value={provider}>
                      {provider}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {providerSearch && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleProviderSearchChange("");
                  }}
                  className={styles.clear_select_button}
                >
                  <X size={14} color="#666" />
                </button>
              )}
            </div>
            <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
              <Select
                value={nameSearch}
                onChange={handleNameSearchChange}
                placeholder="Select Patient Name"
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Patient Name">
                    {nameSearch ? nameSearch : undefined}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent searchField={true}>
                  <SelectItem value="">All Patients</SelectItem>
                  {uniqueNames.map((name) => (
                    <SelectItem key={name} value={name}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {nameSearch && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNameSearchChange("");
                  }}
                  className={styles.clear_select_button}
                >
                  <X size={14} color="#666" />
                </button>
              )}
            </div>
        
            <div className={styles.category_search_wrapper}>
              <input
                type="text"
                className={styles.category_search_input}
                placeholder="Search by Category"
                value={categorySearch}
                onChange={handleCategorySearchChange}
              />
              {categorySearch && (
                <button
                  type="button"
                  onClick={() => setCategorySearch("")}
                  className={styles.clear_button}
                >
                  <X size={14} color="#666" />
                </button>
              )}
              <Search size={18} className={styles.search_icon} />
            </div>
          </div>
        </div>

        <div className={styles.patientInfo}>
          {/* <button
            className={filter === "all" ? styles.filter_btn_active : styles.filter_btn}
            onClick={() => setFilter("all")}
          >
            All Readings
          </button> */}

          <div style={{ position: "relative", minWidth: "150px" }}>
            <Select
              value={dateRange}
              onChange={setDateRange}
              placeholder="Select Date Range"
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Date Range">
                  {dateRange === "1week" ? "1 Week" : dateRange === "1month" ? "1 Month" : dateRange === "3months" ? "3 Months" : dateRange}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1week">1 Week</SelectItem>
                <SelectItem value="1month">1 Month</SelectItem>
                <SelectItem value="3months">3 Months</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button
            variant={filter === "escalated" ? "default" : "ghost"}
            onClick={() => setFilter("escalated")}
          >
            Escalated Readings
          </Button>

          <Button
            variant={filter === "reviewed" ? "default" : "ghost"}
            onClick={() => setFilter("reviewed")}
          >
            Reviewed Readings
          </Button>
        </div>
      
      </div>

      <div className={styles.patient_pae_container}>
        {isReadingsLoading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "40px 0",
            }}
          >
            <Spinner />
          </div>
        ) : (
          <Table
            columns={columns}
            data={filteredData}
            height={400}
            showCheckboxes={true}
            onSelectionChange={handleSelectionChange}
            selectedIds={selectedIds}
            isRowClickable={false}
          />
        )}
      </div>

      <div className={styles.footer_buttons}>
        <Button
          variant="ghost"
          onClick={handleSendSelected}
          disabled={selectedIds.length === 0}
        >
          Send Selected ({selectedIds.length})
        </Button>
        <Button onClick={handleSendAll} disabled={filteredData.length === 0}>
          Send All ({filteredData.length})
        </Button>
      </div>

      <SendToProviderModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedPatients={selectedPatientsToSend}
        type={filter}
      />

      <Modal
        title="Interaction Notes"
        isOpen={isNoteModalOpen}
        onClose={() => setIsNoteModalOpen(false)}
        width="500px"
      >
        <div style={{ padding: "16px 0", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {selectedNote || "_ _"}
        </div>
      </Modal>
    </div>
  );
};

export default OutOfRange;
